import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DesktopAgentService, DesktopOs, InstallPhase } from 'app/services/desktop-agent.service';

type TrackedPhase = Exclude<InstallPhase, 'idle'>;

@Component({
    selector: 'desktop-agent-modal',
    templateUrl: './desktop-agent-modal.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
    ],
})
export class DesktopAgentModalComponent {
    private readonly osLabels: Record<DesktopOs, string> = {
        windows: 'Windows',
        mac: 'macOS',
        linux: 'Linux',
    };

    constructor(public desktopAgentService: DesktopAgentService) {}

    /** Detected OS to lead with in the footer; falls back to Windows when undetectable. */
    get primaryOs(): DesktopOs {
        const os = this.desktopAgentService.getClientOS();
        return os === 'unknown' ? 'windows' : os;
    }

    /** The other platforms, offered as smaller links next to the primary download button. */
    get otherOsList(): DesktopOs[] {
        return (['windows', 'mac', 'linux'] as DesktopOs[]).filter((os) => os !== this.primaryOs);
    }

    osLabel(os: DesktopOs): string {
        return this.osLabels[os];
    }

    installPhaseHeading(phase: TrackedPhase): string {
        switch (phase) {
            case 'downloading': return 'Downloading LollyDrive…';
            case 'downloaded': return 'Waiting for you to open the installer…';
            case 'installing': return 'Installing…';
            case 'signin': return 'Sign in to LollyDrive';
            case 'ready': return 'LollyDrive is ready';
        }
    }

    installPhaseHint(phase: TrackedPhase): string {
        switch (phase) {
            case 'downloading': return 'LollyDrive is being downloaded. This only takes a moment.';
            case 'downloaded': return 'Open the file your browser just downloaded to continue.';
            case 'installing': return 'About 20 seconds. Nothing to choose — no wizard, no options, no restart.';
            case 'signin': return 'LollyDrive is installed. Sign in to LollyDrive to finish.';
            case 'ready': return 'Signed in successfully.';
        }
    }

    /** Spinner while fetching. */
    downloadedStepState(phase: TrackedPhase): 'now' | 'done' {
        return phase === 'downloading' ? 'now' : 'done';
    }

    /** "Downloading 9%", then "Downloaded". */
    downloadedStepLabel(phase: TrackedPhase): string {
        if (phase !== 'downloading') return 'Downloaded';
        const pct = this.desktopAgentService.downloadProgress$.value;
        return pct === null ? 'Downloading' : `Downloading ${pct}%`;
    }

    /** Pending until installer opens. */
    installingStepState(phase: TrackedPhase): 'pending' | 'now' | 'done' {
        if (phase === 'downloading' || phase === 'downloaded') return 'pending';
        if (phase === 'installing') return 'now';
        return 'done';
    }

    /** Active once agent answers. */
    signInStepState(phase: TrackedPhase): 'pending' | 'now' | 'done' {
        if (phase === 'ready') return 'done';
        return phase === 'signin' ? 'now' : 'pending';
    }

    progressWidth(phase: TrackedPhase): number {
        switch (phase) {
            case 'downloading':
            case 'downloaded': return 15;
            case 'installing': return 65;
            case 'signin': return 90;
            case 'ready': return 100;
        }
    }

    closeModal(): void {
        this.desktopAgentService.closeModal();
    }

    openSignIn(): void {
        this.desktopAgentService.reopenSignIn();
    }

    redownload(): void {
        this.desktopAgentService.redownloadDesktopAgent();
    }

    downloadDesktopAgent(downloadType: DesktopOs): void {
        this.desktopAgentService.downloadDesktopAgent(downloadType);
    }
}
